/**
 * Horizontal product carousel — scroll-snap track with prev/next controls.
 */
(function () {
  const SELECTOR = '[data-product-carousel]';
  const CATEGORIES_VISIBLE = 3;
  const SEARCH_MAKE_COLUMNS = 2;
  /** @type {WeakMap<Element, () => void>} */
  const resizeListeners = new WeakMap();

  /** @param {Element} root @param {() => void} handler */
  function setResizeListener(root, handler) {
    const previous = resizeListeners.get(root);
    if (previous) window.removeEventListener('resize', previous);
    resizeListeners.set(root, handler);
    window.addEventListener('resize', handler);
  }

  /** @param {Element} root */
  function clearResizeListener(root) {
    const previous = resizeListeners.get(root);
    if (!previous) return;
    window.removeEventListener('resize', previous);
    resizeListeners.delete(root);
  }

  function getCarouselRoot(track) {
    return track.closest('[data-product-carousel]');
  }

  function getTileStep(track) {
    const card = track.querySelector('.product-card, .testimonial-card, .category-tile, .make-tile');
    if (!card) return track.clientWidth * 0.8;

    const styles = getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const row = card.closest('.search-by-make__row');
    if (row) {
      const rowStyles = getComputedStyle(row);
      const rowGap = parseFloat(rowStyles.columnGap || rowStyles.gap || '0') || 0;
      return card.offsetWidth + rowGap;
    }

    return card.offsetWidth + gap;
  }

  function getScrollStep(track) {
    const root = getCarouselRoot(track);
    const variant = root?.dataset.carouselVariant;
    const tileStep = getTileStep(track);

    if (variant === 'categories' || variant === 'search-category') {
      // Figma 3776:4632 / 3776:4819 — advance one viewport page (~3 tiles @375).
      return tileStep * CATEGORIES_VISIBLE;
    }

    if (variant === 'search-make') {
      // Figma 3776:4892 — advance two columns (2×2 grid page @375).
      return tileStep * SEARCH_MAKE_COLUMNS;
    }

    return tileStep;
  }

  function getPageCount(track) {
    const step = getScrollStep(track);
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 1) return 1;
    return Math.ceil(maxScroll / step) + 1;
  }

  function getActivePage(track) {
    const step = getScrollStep(track);
    if (step <= 0) return 0;
    return Math.round(track.scrollLeft / step);
  }

  function initDots(root, track) {
    const dotsContainer = root.querySelector('[data-carousel-dots]');
    if (!dotsContainer) return;

    const buildDots = () => {
      dotsContainer.innerHTML = '';
      const count = getPageCount(track);

      for (let index = 0; index < count; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product-carousel__dot';
        // Container is aria-hidden (prev/next buttons carry the accessible
        // controls), so dots must stay out of the tab order (WIL-646
        // aria-hidden-focus). Still clickable for pointer users.
        dot.tabIndex = -1;
        dot.setAttribute('aria-label', `Slide ${index + 1}`);
        dot.addEventListener('click', () => {
          track.scrollTo({ left: index * getScrollStep(track), behavior: 'smooth' });
        });
        dotsContainer.appendChild(dot);
      }
    };

    const updateDots = () => {
      const active = getActivePage(track);
      dotsContainer.querySelectorAll('.product-carousel__dot').forEach((dot, index) => {
        const isActive = index === active;
        dot.classList.toggle('product-carousel__dot--active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const refreshDots = () => {
      buildDots();
      updateDots();
    };

    track.addEventListener('scroll', updateDots, { passive: true });
    refreshDots();
    return refreshDots;
  }

  function updateNavState(root) {
    const track = root.querySelector('[data-carousel-track]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;

    const variant = root.dataset.carouselVariant;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= maxScroll - 1;

    root.classList.toggle('is-at-start', atStart);
    root.classList.toggle('is-at-end', atEnd);
    root.classList.toggle('is-static', maxScroll <= 1);

    if (variant === 'testimonials') {
      prev.hidden = false;
      next.hidden = false;
      prev.disabled = atStart;
      next.disabled = atEnd;
      return;
    }

    if (variant === 'categories' || variant === 'products' || variant === 'search-category' || variant === 'search-make') {
      prev.hidden = false;
      next.hidden = false;
      prev.disabled = atStart;
      next.disabled = atEnd;
      return;
    }

    prev.disabled = atStart;
    next.disabled = atEnd;
    prev.hidden = maxScroll <= 1;
    next.hidden = atEnd || maxScroll <= 1;
  }

  function initCarousel(root) {
    const track = root.querySelector('[data-carousel-track]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;

    const variant = root.dataset.carouselVariant;

    initTrackCardLinks(track);

    const scrollByStep = (direction) => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const atStart = track.scrollLeft <= 1;
      const atEnd = track.scrollLeft >= maxScroll - 1;

      if (variant === 'testimonials') {
        if (direction < 0 && atStart) return;
        if (direction > 0 && atEnd) return;
      }

      if (variant === 'categories' || variant === 'products' || variant === 'search-category' || variant === 'search-make') {
        if (direction < 0 && atStart) return;
        if (direction > 0 && atEnd) return;
      }

      track.scrollBy({ left: direction * getScrollStep(track), behavior: 'smooth' });
    };

    prev.addEventListener('click', () => scrollByStep(-1));
    next.addEventListener('click', () => scrollByStep(1));
    track.addEventListener('scroll', () => updateNavState(root), { passive: true });

    let refreshDots = null;
    if (variant === 'categories' || variant === 'products' || variant === 'testimonials' || variant === 'search-category' || variant === 'search-make') {
      refreshDots = initDots(root, track);
    }

    setResizeListener(root, () => {
      updateNavState(root);
      if (typeof refreshDots === 'function') refreshDots();
    });

    updateNavState(root);
  }

  /** Suppress card navigation when the pointer gesture was a carousel drag, not a tap */
  function initTrackCardLinks(track) {
    if (track.dataset.carouselLinksInit === 'true') return;
    track.dataset.carouselLinksInit = 'true';

    /** @type {{ x: number; y: number } | null} */
    let pointerStart = null;
    let didDrag = false;
    const dragThreshold = 8;

    track.addEventListener(
      'pointerdown',
      (event) => {
        pointerStart = { x: event.clientX, y: event.clientY };
        didDrag = false;
      },
      { passive: true }
    );

    track.addEventListener(
      'pointermove',
      (event) => {
        if (!pointerStart) return;
        if (
          Math.abs(event.clientX - pointerStart.x) > dragThreshold ||
          Math.abs(event.clientY - pointerStart.y) > dragThreshold
        ) {
          didDrag = true;
        }
      },
      { passive: true }
    );

    track.addEventListener(
      'pointerup',
      () => {
        window.setTimeout(() => {
          pointerStart = null;
          didDrag = false;
        }, 0);
      },
      { passive: true }
    );

    track.addEventListener(
      'click',
      (event) => {
        if (!didDrag) return;
        if (!(event.target instanceof Element)) return;
        const link = event.target.closest('.product-card__link');
        if (link instanceof HTMLAnchorElement) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );
  }

  function initRoot(root) {
    // The script tag is emitted by every carousel section, so this file can
    // execute several times on one page — only bind each carousel once.
    if (root.dataset.carouselBound) return;
    root.dataset.carouselBound = 'true';

    initCarousel(root);
  }

  document.querySelectorAll(SELECTOR).forEach(initRoot);

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;
    event.target.querySelectorAll(SELECTOR).forEach(initRoot);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches(SELECTOR)) clearResizeListener(event.target);
    event.target.querySelectorAll(SELECTOR).forEach(clearResizeListener);
  });
})();
